import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type UsageRow = {
  id: string;
  user_id: string | null;
  model: string;
  call_type: string;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
  status: string;
  error: string | null;
  created_at: string;
};

export type TryonRow = { id: string; user_id: string; created_at: string };
export type OrderRow = { amount_cents: number; credits: number; created_at: string; user_id: string };

export interface PeriodData {
  usage: UsageRow[];
  tryons: TryonRow[];
  orders: OrderRow[];
}

export interface AdminMetricsResult {
  current: PeriodData;
  previous: PeriodData;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  lastUpdated: Date | null;
}

function getAdminMetricsErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === "object") {
    const maybeError = error as { message?: unknown; details?: unknown; hint?: unknown; code?: unknown };
    const parts = [maybeError.message, maybeError.details, maybeError.hint]
      .filter((part): part is string => typeof part === "string" && part.trim().length > 0);
    if (parts.length > 0) return parts.join(" ");
    if (typeof maybeError.code === "string") return `Dashboard query failed (${maybeError.code}).`;
  }
  return typeof error === "string" && error.trim().length > 0
    ? error
    : "Dashboard query failed. Please refresh and try again.";
}

async function fetchPeriod(fromISO: string, toISO: string): Promise<PeriodData> {
  // Page through gemini_usage in case >5000 rows.
  const usage: UsageRow[] = [];
  const PAGE = 1000;
  let offset = 0;
  // Cap at 10k rows to avoid runaway pulls.
  for (let i = 0; i < 10; i++) {
    const { data, error } = await supabase
      .from("gemini_usage")
      .select(
        "id,user_id,model,call_type,input_tokens,output_tokens,estimated_cost_usd,status,error,created_at",
      )
      .gte("created_at", fromISO)
      .lt("created_at", toISO)
      .order("created_at", { ascending: false })
      .range(offset, offset + PAGE - 1);
    if (error) throw error;
    const batch = (data ?? []) as UsageRow[];
    usage.push(...batch);
    if (batch.length < PAGE) break;
    offset += PAGE;
  }

  const [tryonsRes, ordersRes] = await Promise.all([
    supabase
      .from("tryons")
      .select("id,user_id,created_at")
      .gte("created_at", fromISO)
      .lt("created_at", toISO)
      .limit(10000),
    supabase
      .from("paddle_orders")
      .select("amount_cents,credits,created_at,user_id")
      .eq("status", "paid")
      .gte("created_at", fromISO)
      .lt("created_at", toISO)
      .limit(10000),
  ]);
  if (tryonsRes.error) throw tryonsRes.error;
  if (ordersRes.error) throw ordersRes.error;

  return {
    usage,
    tryons: (tryonsRes.data ?? []) as TryonRow[],
    orders: (ordersRes.data ?? []) as OrderRow[],
  };
}

export function useAdminMetrics(
  from: Date,
  to: Date,
  enabled: boolean,
): AdminMetricsResult {
  const [current, setCurrent] = useState<PeriodData>({ usage: [], tryons: [], orders: [] });
  const [previous, setPrevious] = useState<PeriodData>({ usage: [], tryons: [], orders: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    setLoading(true);
    setError(null);

    const lengthMs = to.getTime() - from.getTime();
    const prevTo = new Date(from.getTime());
    const prevFrom = new Date(from.getTime() - lengthMs);

    Promise.all([
      fetchPeriod(from.toISOString(), to.toISOString()),
      fetchPeriod(prevFrom.toISOString(), prevTo.toISOString()),
    ])
      .then(([cur, prev]) => {
        if (!active) return;
        setCurrent(cur);
        setPrevious(prev);
        setLastUpdated(new Date());
      })
      .catch((e) => {
        if (!active) return;
        setError(getAdminMetricsErrorMessage(e));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [from.getTime(), to.getTime(), enabled, tick]);

  return { current, previous, loading, error, refresh, lastUpdated };
}
